import { useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Dropdown } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { MobileBackButton } from '../../assets/icons/common/common-icons';
import { ActivationChecklistPane } from '../activation/components/checklist-pane/ChecklistPane';
import { useStore } from '../app/data/root.store';
import { AppHeader } from '../layout/components/Header/Header';
import { RightSidePushMenuWrapper } from '../layout/components/Layout.styles';
import { MobileBackButtonWrapper } from '../layout/components/MobileLayout.styles';
import { EntitiesFlowContent } from './EntitiesFlowContent';
import {
  EntitiesMobileHeader,
  EntitiesPageContainer,
  GuideArea,
  MobileAddButton,
  MobileHeaderControls,
  MobileHeaderTitle,
  MobileThreeDotsButton,
  PageWrapper
} from './EntitiesPage.styles';
import { AddIcon, ArrowIcon, ThreeDotsIcon, MobileFitIcon, MobileRearrangeIcon, MobileDownloadIcon, MobileListIcon } from './assets/icons';
import { DropdownMenu, DropdownMenuItem } from './components/EntitiesHeader/EntitiesHeader.styles';
import { EntityTreeStructureGuide } from './components/EntityTreeStructureGuide/EntityTreeStructureGuide';
import { useAnalytics } from '../app/data/AnalyticsProvider';
import { createEntityManagementCTAClickEvent } from './data/entities.mixpanel-events';

export const EntitiesPage = observer(() => {
  const { userStore, entitiesStore, uiStore } = useStore();
  const { mixpanelTrack } = useAnalytics();
  const { fitView } = useReactFlow();

  const isEntityTreeStructureGuideVisible = entitiesStore.graphData.edges.length < 1 && uiStore.isEntitiesPageThreshold;

  const handleLearnMore = () => {
    window.open('https://help.vyzer.co/en/articles/11840820-entities-visualize-and-manage-ownership-across-your-portfolio', '_blank');
  };

  const handleAddEntity = () => {
    if (userStore.isDemoMode) {
      uiStore.setShowDemoLimitationModal(true);
      return;
    } else if (userStore.isReadOnlyMode || userStore.isActionBlockedByRole('addHoldingEntity')) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }

    mixpanelTrack('CTA Clicked', createEntityManagementCTAClickEvent('Add entity', 'Entity Management Bar'));
    entitiesStore.setShowAddModal(true);
  };



  const hasNodes = entitiesStore.graphData.nodes.length > 0;

  const handleRearrangeClick = () => {
    if (userStore.isActionBlockedByRole('editHoldingEntity') || userStore.isReadOnlyMode) {
      userStore.setShowLimitedSharedAccess(true);
      return;
    }
    mixpanelTrack('CTA Clicked', createEntityManagementCTAClickEvent('Rearrange structure', 'Entity Management Bar'));
    entitiesStore.handleRearrange();
    fitView({ duration: 200, padding: 0.1 });
  };

  const handleDownloadClick = () => {
    if (hasNodes) {
      mixpanelTrack('CTA Clicked', createEntityManagementCTAClickEvent('Export structure', 'Entity Management Bar'));
      entitiesStore.handleDownload();
    }
  };

  const dropdownMenu = (
    <DropdownMenu>
      <DropdownMenuItem onClick={entitiesStore.handleShowEntityList}>
        <MobileListIcon style={{ flexShrink: 0 }} /> List view <ArrowIcon style={{ marginLeft: 'auto', width: 16, height: 16 }} />
      </DropdownMenuItem>
      <DropdownMenuItem onClick={() => fitView({ duration: 200, padding: 0.1 })}>
        <MobileFitIcon style={{ flexShrink: 0, width: 24, height: 24 }} />
        Fit view
      </DropdownMenuItem>
      <DropdownMenuItem onClick={handleRearrangeClick}>
        <MobileRearrangeIcon />
        Rearrange structure
      </DropdownMenuItem>
      <DropdownMenuItem
        onClick={handleDownloadClick}
        disabled={!hasNodes}
      >
        <MobileDownloadIcon />
        Download structure
      </DropdownMenuItem>
    </DropdownMenu>
  );

  return (
    <>
      {uiStore.isDesktopView ? <AppHeader /> :
        <EntitiesMobileHeader isHeaderFixed={uiStore.isMobileHeaderFixed}>
          <MobileBackButtonWrapper onClick={() => { uiStore.setIsAppMobileMenuOpen(true) }}>
            <MobileBackButton />
          </MobileBackButtonWrapper>
          <MobileHeaderTitle>
            Entity Management
          </MobileHeaderTitle>
          <MobileHeaderControls>
            <MobileAddButton onClick={handleAddEntity}>
              <AddIcon />
              Add
            </MobileAddButton>
            <Dropdown
              overlay={dropdownMenu}
              trigger={['click']}
              placement="bottomRight"
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              <MobileThreeDotsButton>
                <ThreeDotsIcon />
              </MobileThreeDotsButton>
            </Dropdown>
          </MobileHeaderControls>
        </EntitiesMobileHeader>}
      <RightSidePushMenuWrapper>
        {userStore.isViewingOwnPortfolio && userStore.isGetStarted && <ActivationChecklistPane />}
      </RightSidePushMenuWrapper>
      <PageWrapper style={{ paddingBottom: 0, paddingTop: 0 }}>
        <EntitiesPageContainer isGuideVisible={isEntityTreeStructureGuideVisible}>
          <EntitiesFlowContent />

          {isEntityTreeStructureGuideVisible && (
            <GuideArea>
              <EntityTreeStructureGuide onLearnMore={handleLearnMore} />
            </GuideArea>
          )}
        </EntitiesPageContainer>
      </PageWrapper>
    </>
  );
});